import React from 'react';
import { useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { useTranslation } from 'react-i18next';

import formatGroupedNumber from 'services/numbers/formatGroupedNumber';
import getContributionMarginTotal from 'services/products/calculations/getContributionMarginTotal';
import getExpenseTotal from 'services/products/calculations/getExpenseTotal';
import getOfferQuantities from 'services/offers/getOfferQuantities';
import getPositionHours, {
  isAverageHourlyEarningsMissing,
} from 'services/offers/getPositionHours';
import getSellingPriceTotal from 'services/products/calculations/getSellingPriceTotal';
import IInstallation from 'services/products/calculations/IInstallation';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import ISubproduct from 'services/products/calculations/ISubproduct';
import ReadOnlyTextInput from 'components/layout/forms/ReadOnlyTextInput';
import translations from 'services/translations/translations';

import 'style/offers/OfferProductPriceSummary.css';

interface IProductPrimaryVersion {
  installations: IInstallation[];
  quantity: number;
  subproducts: ISubproduct[];
}

const OfferProductPriceSummary = ({
  products,
  title,
}: {
  products: IOfferProductsGet[];
  title?: string;
}): JSX.Element => {
  const { t } = useTranslation([translations.common, translations.offers]);

  const installationCategories = useSelector(
    (state: any) => state.products.installationCategories ?? []
  );

  const productPrimaryVersions = products.map(
    (product): IProductPrimaryVersion => {
      const primaryVersion = product.versions.find(
        (version) => version.isPrimary
      );

      if (primaryVersion === undefined) {
        return {
          installations: [],
          quantity: product.quantity,
          subproducts: [],
        };
      }

      return {
        installations: primaryVersion.installations,
        quantity: product.quantity,
        subproducts: primaryVersion.subproducts,
      };
    }
  );

  const sum = (
    calculate: (
      installations: IInstallation[],
      subproducts: ISubproduct[],
      quantity: number
    ) => number
  ) =>
    productPrimaryVersions.reduce(
      (total, version) =>
        calculate(
          version.installations,
          version.subproducts,
          version.quantity
        ) + total,
      0
    );

  const sellingPriceSum = sum(getSellingPriceTotal);
  const expenseSum = sum(getExpenseTotal);
  const contributionMargin = sum(getContributionMarginTotal);
  const contributionMarginRatio =
    sellingPriceSum === 0 ? 0 : (contributionMargin / sellingPriceSum) * 100;

  // Installation hours of the positions and the contribution margin for them
  const hours = products.reduce((total, product) => {
    const primaryVersion = product.versions.find(
      (version) => version.isPrimary
    );

    return primaryVersion === undefined
      ? total
      : total +
          getPositionHours(
            primaryVersion.installations,
            primaryVersion.averageHourlyEarnings,
            product.quantity,
            installationCategories
          );
  }, 0);

  const contributionMarginPerHour =
    hours === 0 ? null : contributionMargin / hours;

  // Positions with installation work but no average hourly earnings have no hours, which
  // makes the contribution margin per hour too high
  const positionsMissingAverageHourlyEarnings = products.filter((product) => {
    const primaryVersion = product.versions.find(
      (version) => version.isPrimary
    );

    return (
      primaryVersion !== undefined &&
      isAverageHourlyEarningsMissing(
        primaryVersion.installations,
        primaryVersion.averageHourlyEarnings,
        installationCategories
      )
    );
  }).length;

  // Quantities by unit, batch positions by their batch calculation review
  const quantities = Object.entries(getOfferQuantities(products))
    .map(
      ([unit, quantity]) =>
        `${formatGroupedNumber(quantity, 2, 0)} ${t(
          `${translations.common}:units:${unit}`
        )}`
    )
    .join(', ');

  return (
    <div className="offer-product-price-summary">
      <Row>
        <Col>
          <h3 className="compact-header">
            {title == null
              ? t(`${translations.common}:Price`).toUpperCase()
              : title}
          </h3>
        </Col>
      </Row>
      <Row>
        <Col md="3" lg="2">
          <ReadOnlyTextInput label={t(`${translations.offers}:Selling price`)}>
            {`${formatGroupedNumber(sellingPriceSum)} €`}
          </ReadOnlyTextInput>
        </Col>
        <Col md="3" lg="2">
          <ReadOnlyTextInput label={t(`${translations.offers}:Expenses total`)}>
            {`${formatGroupedNumber(expenseSum)} €`}
          </ReadOnlyTextInput>
        </Col>
        <Col md="3" lg="2">
          <ReadOnlyTextInput
            label={`${t(`${translations.offers}:Contribution margin`)} €`}
          >
            {`${formatGroupedNumber(contributionMargin)} €`}
          </ReadOnlyTextInput>
        </Col>
        <Col md="3" lg="2">
          <ReadOnlyTextInput
            label={`${t(`${translations.offers}:Contribution margin`)} %`}
          >
            {`${formatGroupedNumber(contributionMarginRatio)} %`}
          </ReadOnlyTextInput>
        </Col>
        <Col md="3" lg="2">
          <ReadOnlyTextInput label={t(`${translations.offers}:Hours`)}>
            {`${formatGroupedNumber(hours)} h`}
          </ReadOnlyTextInput>
        </Col>
        <Col md="3" lg="2">
          <ReadOnlyTextInput
            label={t(`${translations.offers}:Contribution margin per hour`)}
          >
            {contributionMarginPerHour === null
              ? '-'
              : `${formatGroupedNumber(contributionMarginPerHour)} €/h`}
          </ReadOnlyTextInput>
        </Col>
      </Row>
      {positionsMissingAverageHourlyEarnings > 0 ? (
        <Row>
          <Col>
            <span className="missing-average-hourly-earnings">
              {t(
                `${translations.offers}:Positions missing average hourly earnings`,
                {
                  count: positionsMissingAverageHourlyEarnings,
                }
              )}
            </span>
          </Col>
        </Row>
      ) : null}
      <Row>
        <Col>
          <ReadOnlyTextInput label={t(`${translations.offers}:Quantities`)}>
            {quantities === '' ? '-' : quantities}
          </ReadOnlyTextInput>
        </Col>
      </Row>
    </div>
  );
};

export default OfferProductPriceSummary;
